import Building02Icon from "@redotech/redo-icons/building-02.svg";
import Building02FilledIcon from "@redotech/redo-icons/building-02_filled.svg";
import LogOut04Icon from "@redotech/redo-icons/log-out-04.svg";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import {
  type PrimaryNavGroup,
  RedoPrimaryNavRail,
} from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav-rail";
import { getGravatarUrlFromEmail } from "@redotech/redo-web/gravatar-utils";
import {
  type NavigationUserProfile,
  UserProfile,
  UserProfileContext,
} from "@redotech/redo-web/navigation/common/redo-nav-user-profile";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAuthData } from "../auth/auth-data-context";
import { useAuthToken } from "../auth/auth-token-context";
import { recordLogout } from "../auth/logout";

const NAV_GROUPS: PrimaryNavGroup[] = [
  {
    id: "main",
    tabs: [
      {
        id: "stores",
        type: "link",
        href: "/stores",
        pathRoot: "/stores",
        friendlyName: "Stores",
        UnfilledIcon: Building02Icon,
        FilledIcon: Building02FilledIcon,
        hasNotification: false,
      },
    ],
  },
];

/** The signed-in partner user, with Sign out in its menu, as admin's nav does. */
const PartnerUserProfile = memo(function PartnerUserProfile() {
  const { partnerUser } = useAuthData();
  const { token, clear } = useAuthToken();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const email = partnerUser?.email ?? "";

  const items: RedoListItem<() => void>[] = useMemo(
    () => [
      {
        id: "sign-out",
        type: "text",
        text: "Sign out",
        leadingItem: { type: "icon", Icon: LogOut04Icon },
        value: () => {
          void recordLogout(token, queryClient).then(clear);
        },
      },
    ],
    [token, queryClient, clear],
  );

  const profile: NavigationUserProfile = {
    name: partnerUser?.name ?? email,
    teamName: partnerUser?.partner.name ?? "",
    profileImageUrl: getGravatarUrlFromEmail(email, 48, "identicon"),
    profileDropdownOpen: open,
    setProfileDropdownOpen: setOpen,
    setDropdownAnchor: setAnchor,
  };

  return (
    <UserProfileContext.Provider value={profile}>
      <UserProfile collapsed />
      <RedoListDropdown<() => void>
        darkMode
        dropdownAnchor={anchor}
        dropdownOpen={open}
        items={items}
        itemSelected={({ item }) => item.value()}
        setDropdownOpen={setOpen}
        size="sm"
      />
    </UserProfileContext.Provider>
  );
});

export const SideNav = memo(function SideNav() {
  const { pathname } = useLocation();
  const selected = NAV_GROUPS.flatMap((group) => group.tabs).find(
    (tab) => tab.type === "link" && pathname.startsWith(tab.pathRoot),
  );
  return (
    <RedoPrimaryNavRail
      footer={<PartnerUserProfile />}
      groups={NAV_GROUPS}
      selectedTabId={selected?.id}
    />
  );
});
